import React, {
  createContext,
  useCallback,
  useState,
  type PropsWithChildren,
} from 'react';
import { StyleSheet } from 'react-native';
import { Snackbar as PaperSnackbar, Portal } from 'react-native-paper';
import { Colors } from '../constants/Colors';
import { useDynamicStyles } from '../hooks/useDynamicStyles';
import type { WithResponsive } from '../utils/responsivity';
import { useSafeContext } from './useSafeContext';

type SnackbarType = 'error' | 'info' | 'success';

type SnackbarState = {
  text: string;
  type: SnackbarType;
  actionLabel?: string;
};

type SnackbarContextType = {
  snackbar: SnackbarState | undefined;
  showSnackbar: (snackbar: SnackbarState) => void;
  hideSnackbar: () => void;
};

// @ts-expect-error -- don't use default, instead console error if component not used in appropriate context
export const SnackbarContext = createContext<SnackbarContextType>();
SnackbarContext.displayName = 'SnackbarContext';

export const SnackbarProvider = ({ children }: PropsWithChildren) => {
  const styles = useDynamicStyles(createStyles);
  const [snackbar, setSnackbar] = useState<SnackbarState | undefined>();

  const hideSnackbar = useCallback(() => {
    setSnackbar(undefined);
  }, []);

  const showSnackbar = useCallback((newSnackbar: SnackbarState) => {
    setSnackbar(newSnackbar);
  }, []);

  return (
    <SnackbarContext.Provider value={{ snackbar, showSnackbar, hideSnackbar }}>
      {children}
      <Portal>
        <PaperSnackbar
          style={styles.snackbar}
          visible={Boolean(snackbar)}
          onDismiss={hideSnackbar}
          action={{
            label: snackbar?.actionLabel ?? 'Ok',
            onPress: hideSnackbar,
            textColor: Colors.tint,
          }}
          duration={3500}
          theme={{
            colors: {
              inverseSurface: Colors.purple1opacity50,
              inverseOnSurface: Colors.tint,
            },
          }}
        >
          {snackbar?.text}
        </PaperSnackbar>
      </Portal>
    </SnackbarContext.Provider>
  );
};

const createStyles = ({ isMobile, isTablet }: WithResponsive) =>
  StyleSheet.create({
    snackbar: {
      margin: isMobile ? 12 : isTablet ? 16 : 20,
      borderRadius: isMobile ? 16 : 30,
      width: isMobile ? undefined : isTablet ? '50%' : '30%',
    },
  });

export const useSnackbar = () => {
  return useSafeContext(SnackbarContext);
};
